import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Search, Loader2, Check, ArrowLeft, Phone, Users, X, Plus, Paperclip } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, LoadingSpinner } from './components';

function MessagingPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastMode, setBroadcastMode] = useState('all'); // 'all' or 'select'
  const [techList, setTechList] = useState([]);
  const [selectedTechs, setSelectedTechs] = useState(new Set());
  const [loadingTechs, setLoadingTechs] = useState(false);
  const [showAutomated, setShowAutomated] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [directNumber, setDirectNumber] = useState('');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, personId }
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const searchTimeout = useRef(null);

  const fetchUnreadCounts = async () => {
    try {
      const data = await apiService.getUnreadCounts();
      setUnreadCounts(data.by_person || {});
      setTotalUnread(data.total_unread || 0);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    fetchRecentConversations();
    fetchUnreadCounts();
    const interval = setInterval(() => { refreshConversations(); fetchUnreadCounts(); }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Poll active thread for new messages
  useEffect(() => {
    if (!selectedPerson) return;
    const interval = setInterval(() => refreshMessages(selectedPerson.id), 10000);
    return () => clearInterval(interval);
  }, [selectedPerson?.id, showAutomated]);

  // Re-fetch when toggling automated messages
  useEffect(() => {
    if (selectedPerson) {
      selectPerson(selectedPerson);
    }
  }, [showAutomated]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (selectedPerson && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selectedPerson, loadingMessages]);

  const buildConversationList = (results) => {
    const personMap = new Map();
    for (const interaction of results) {
      if (!interaction.person) continue;
      const pid = interaction.person.id;
      const isAutomated = !!interaction.metadata?.automated;
      if (!personMap.has(pid)) {
        personMap.set(pid, {
          person: {
            ...interaction.person,
            phone_number: interaction.effective_phone || interaction.person.phone_number,
            display_name: interaction.effective_name || interaction.person.full_name,
            contact_info: interaction.contact_info || null,
          },
          lastMessage: isAutomated ? null : interaction.content,
          lastDate: isAutomated ? null : interaction.date_time,
          sortDate: isAutomated ? null : interaction.date_time,
          direction: isAutomated ? 'outgoing' : (interaction.metadata?.direction || 'incoming'),
        });
      } else {
        const entry = personMap.get(pid);
        if (!isAutomated) {
          if (!entry.lastMessage) {
            entry.lastMessage = interaction.content;
            entry.direction = interaction.metadata?.direction || 'incoming';
          }
          if (!entry.sortDate) {
            entry.sortDate = interaction.date_time;
            entry.lastDate = interaction.date_time;
          }
        }
      }
    }
    return Array.from(personMap.values())
      .sort((a, b) => {
        // Sort by non-automated date; conversations with only automated messages go to bottom
        const da = a.sortDate ? new Date(a.sortDate) : new Date(0);
        const db = b.sortDate ? new Date(b.sortDate) : new Date(0);
        return db - da;
      });
  };

  const fetchRecentConversations = async () => {
    setLoading(true);
    try {
      // Fetch recent conversations and unread ones separately to ensure
      // unread messages always appear even if buried by volume
      const [recentData, unreadData] = await Promise.all([
        apiService.getInteractions({
          type: 'sms',
          ordering: '-date_time',
          page_size: 100,
        }),
        apiService.getInteractions({
          type: 'sms',
          ordering: '-date_time',
          is_read: false,
          page_size: 50,
        }),
      ]);
      // Merge results, deduplicating by interaction id
      const seen = new Set();
      const merged = [];
      for (const item of [...(recentData.results || []), ...(unreadData.results || [])]) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          merged.push(item);
        }
      }
      setConversations(buildConversationList(merged));
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshConversations = async () => {
    try {
      const data = await apiService.getInteractions({
        type: 'sms',
        ordering: '-date_time',
        page_size: 100,
      });
      setConversations(buildConversationList(data.results || []));
    } catch (err) {
      // silent
    }
  };

  const refreshMessages = async (personId) => {
    try {
      const params = showAutomated ? '?include_automated=true' : '';
      const data = await apiService.get(`/amn/messaging/conversation/${personId}/${params}`);
      const newMsgs = data.messages || [];
      setMessages(prev => {
        if (newMsgs.length !== prev.length) return newMsgs;
        return prev;
      });
    } catch (err) {
      // silent
    }
  };

  const selectPerson = async (person) => {
    setShowNewMessage(false);
    setSelectedPerson(person);
    setLoadingMessages(true);
    setError(null);
    // Clear unread count locally (backend marks as read on fetch)
    setUnreadCounts(prev => {
      const next = { ...prev };
      delete next[String(person.id)];
      return next;
    });
    setTotalUnread(prev => Math.max(0, prev - (unreadCounts[String(person.id)] || 0)));
    try {
      const params = showAutomated ? '?include_automated=true' : '';
      const data = await apiService.get(`/amn/messaging/conversation/${person.id}/${params}`);
      setMessages(data.messages || []);
      setSelectedPerson(prev => ({
        ...prev,
        client_id: data.client_id || prev.client_id,
        phone_number: data.phone_number || prev.phone_number,
        display_name: data.person_name || prev.display_name,
        contact_info: data.contact_info || prev.contact_info || null,
      }));
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiService.get('/amn/persons/', { search: query, page_size: 10 });
        setSearchResults(data.results || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const compressImage = (file, maxDim = 1200, quality = 0.85) => new Promise((resolve) => {
    if (file.size < 500 * 1024) { resolve(file); return; }
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const uploadImage = async (file) => {
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file);
      const preview = URL.createObjectURL(compressed);
      setImagePreview(preview);
      const formData = new FormData();
      formData.append('image', compressed);
      const resp = await apiService.post('/amn/messaging/upload-image/', formData, true);
      setImageUrl(resp.url);
    } catch (err) {
      setError('Failed to upload image. ' + (err.message || ''));
      setImagePreview(null);
      setImageUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const fileInputRef = useRef(null);

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    // Debug: log clipboard contents
    const types = Array.from(items).map(i => `${i.kind}:${i.type}`);
    console.log('Paste clipboard items:', types);
    // Check for image file in clipboard (standard copy-image)
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) uploadImage(file);
        return;
      }
    }
    // Check for HTML with an img tag (some browsers put copied images as HTML)
    const html = e.clipboardData?.getData('text/html') || '';
    const imgMatch = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (imgMatch) {
      const src = imgMatch[1];
      e.preventDefault();
      if (src.startsWith('data:image/')) {
        try {
          const [header, b64] = src.split(',');
          const mime = header.match(/data:(image\/[\w+]+)/)?.[1] || 'image/png';
          const bytes = atob(b64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const ext = mime.split('/')[1].replace('+xml', '');
          const file = new File([arr], `pasted-image.${ext}`, { type: mime });
          uploadImage(file);
        } catch (err) {
          console.error('Failed to parse pasted image data:', err);
        }
      } else if (src.startsWith('http')) {
        fetch(src).then(r => r.blob()).then(blob => {
          const ext = (blob.type || 'image/png').split('/')[1];
          const file = new File([blob], `pasted-image.${ext}`, { type: blob.type || 'image/png' });
          uploadImage(file);
        }).catch(err => console.error('Failed to fetch pasted image URL:', err));
      }
      return;
    }
    // Check for base64 data URL pasted as plain text
    const text = e.clipboardData?.getData('text/plain') || '';
    if (text.startsWith('data:image/')) {
      e.preventDefault();
      try {
        const [header, b64] = text.split(',');
        const mime = header.match(/data:(image\/[\w+]+)/)?.[1] || 'image/png';
        const bytes = atob(b64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const file = new File([arr], `pasted-image.${mime.split('/')[1]}`, { type: mime });
        uploadImage(file);
      } catch (err) {
        console.error('Failed to parse pasted image data:', err);
      }
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageUrl(null);
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !imageUrl) || sending) return;

    if (showNewMessage) {
      if (!directNumber.trim()) return;
      setError(null);
      setSending(true);
      try {
        const payload = { to_number: directNumber.trim(), message: newMessage.trim() || (imageUrl ? 'Image attached' : '') };
        if (imageUrl) payload.media_urls = [imageUrl];
        const result = await apiService.post('/amn/messaging/send/', payload);
        setSent(true);
        setNewMessage('');
        setImagePreview(null);
        setImageUrl(null);
        setShowNewMessage(false);
        setDirectNumber('');
        fetchRecentConversations();
        if (result.person_id) {
          const data = await apiService.get(`/amn/messaging/conversation/${result.person_id}/`);
          setSelectedPerson({ id: result.person_id, first_name: result.person_name || '', phone_number: result.to_number });
          setMessages(data.messages || []);
        }
        setTimeout(() => setSent(false), 2000);
      } catch (err) {
        setError(err.message || 'Failed to send message');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!selectedPerson) return;
    setError(null);
    setSending(true);

    try {
      const payload = { person_id: selectedPerson.id, message: newMessage.trim() || (imageUrl ? 'Image attached' : '') };
      if (imageUrl) payload.media_urls = [imageUrl];
      await apiService.post('/amn/messaging/send/', payload);
      setSent(true);
      setNewMessage('');
      setImagePreview(null);
      setImageUrl(null);

      // Refresh messages
      const data = await apiService.get(`/amn/messaging/conversation/${selectedPerson.id}/`);
      setMessages(data.messages || []);

      // Update conversation list
      fetchRecentConversations();

      setTimeout(() => setSent(false), 2000);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const fetchTechnicians = async () => {
    setLoadingTechs(true);
    try {
      const data = await apiService.getTechnicians();
      const techs = (data.results || data || []).filter(t => t.square_status === 'active' && t.internal_status !== 'inactive');
      setTechList(techs);
    } catch (err) {
      console.error('Failed to load technicians:', err);
    } finally {
      setLoadingTechs(false);
    }
  };

  const toggleTech = (id) => {
    setSelectedTechs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim() || broadcastSending) return;
    if (broadcastMode === 'select' && selectedTechs.size === 0) return;
    setBroadcastSending(true);
    setBroadcastResult(null);
    try {
      const payload = { message: broadcastMessage.trim() };
      if (broadcastMode === 'select') {
        payload.staff_ids = Array.from(selectedTechs);
      }
      const data = await apiService.post('/amn/messaging/broadcast/technicians/', payload);
      setBroadcastResult(data);
      if (data.sent_count > 0) {
        setBroadcastMessage('');
        fetchRecentConversations();
      }
    } catch (err) {
      setBroadcastResult({ error: err.message || 'Failed to send broadcast' });
    } finally {
      setBroadcastSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
  };

  const markUnread = async (personId) => {
    try {
      await apiService.post(`/amn/messaging/conversation/${personId}/mark-unread/`);
      setUnreadCounts(prev => ({ ...prev, [String(personId)]: 1 }));
      setTotalUnread(prev => prev + 1);
      setContextMenu(null);
      // Deselect if we're marking the current conversation
      if (selectedPerson?.id === personId) {
        setSelectedPerson(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to mark unread:', err);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }
    return phone;
  };

  const personName = (p) => {
    if (p.display_name) return p.display_name;
    if (p.first_name && p.last_name) return `${p.first_name} ${p.last_name}`;
    if (p.first_name) return p.first_name;
    return formatPhone(p.phone_number) || 'Unknown';
  };

  const charCount = newMessage.length;
  const segments = Math.ceil(charCount / 160) || 1;

  return (
    <div className="flex-1 flex flex-col overflow-hidden" onClick={() => contextMenu && setContextMenu(null)}>
      {/* Right-click context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => markUnread(contextMenu.personId)}
            className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
            Mark as Unread
          </button>
        </div>
      )}
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedPerson && (
              <button
                onClick={() => setSelectedPerson(null)}
                className="p-1 rounded hover:bg-gray-100 md:hidden"
              >
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              </button>
            )}
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
            {totalUnread > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full min-w-[18px] text-center">
                {totalUnread}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowNewMessage(true); setSelectedPerson(null); setDirectNumber(''); setNewMessage(''); setError(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New</span>
            </button>
            <button
              onClick={() => { setShowBroadcast(true); setBroadcastResult(null); setBroadcastMode('all'); setSelectedTechs(new Set()); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Text Team</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Conversation List */}
        <div className={`w-full md:w-80 md:min-w-[320px] md:max-w-[320px] border-r border-gray-200 bg-white flex flex-col overflow-hidden ${selectedPerson ? 'hidden md:flex' : 'flex'}`}>
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search contacts..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searching && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>
          </div>

          {/* Search Results or Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {searchResults ? (
              <>
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <p className="text-xs text-gray-500 font-medium">Search Results ({searchResults.length})</p>
                </div>
                {searchResults.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6">No contacts found</p>
                ) : (
                  searchResults.map((person) => (
                    <button
                      key={person.id}
                      onClick={() => {
                        selectPerson(person);
                        setSearchQuery('');
                        setSearchResults(null);
                      }}
                      className="w-full px-3 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900">{personName(person)}</p>
                      <p className="text-xs text-gray-500">{formatPhone(person.phone_number)}</p>
                    </button>
                  ))
                )}
              </>
            ) : loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 px-4">
                <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No conversations yet</p>
                <p className="text-xs text-gray-400 mt-1">Search for a contact to start messaging</p>
              </div>
            ) : (
              [...conversations]
                .sort((a, b) => {
                  const aUnread = unreadCounts[String(a.person.id)] || 0;
                  const bUnread = unreadCounts[String(b.person.id)] || 0;
                  if (aUnread && !bUnread) return -1;
                  if (!aUnread && bUnread) return 1;
                  return new Date(b.lastDate) - new Date(a.lastDate);
                })
                .map((conv) => {
                const unread = unreadCounts[String(conv.person.id)] || 0;
                return (
                <button
                  key={conv.person.id}
                  onClick={() => selectPerson(conv.person)}
                  onContextMenu={(e) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, personId: conv.person.id }); }}
                  className={`w-full min-w-0 px-3 py-2.5 text-left border-b border-gray-100 transition-colors ${
                    selectedPerson?.id === conv.person.id
                      ? 'bg-blue-50 border-l-2 border-l-blue-600'
                      : unread > 0
                        ? 'bg-red-50/50 hover:bg-red-50'
                        : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="truncate">
                      <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
                        {personName(conv.person)}
                      </p>
                      {conv.person.contact_info && (
                        <p className="text-[10px] text-purple-600 truncate">
                          via {conv.person.contact_info.name} ({conv.person.contact_info.relationship})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {unread > 0 && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full min-w-[18px] text-center">
                          {unread}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400">
                        {formatTime(conv.lastDate)}
                      </span>
                    </div>
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                    {conv.direction === 'outgoing' ? 'You: ' : ''}{conv.lastMessage}
                  </p>
                </button>
                );
              })
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className={`flex-1 min-w-0 flex flex-col bg-gray-50 ${selectedPerson || showNewMessage ? 'flex' : 'hidden md:flex'}`}>
          {showNewMessage && !selectedPerson ? (
            <>
              {/* New Message Header */}
              <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Plus className="w-4 h-4 text-blue-700" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">New Message</p>
                </div>
                <button
                  onClick={() => setShowNewMessage(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Phone Input + Compose */}
              <div className="flex-1 flex flex-col">
                <div className="px-4 py-3 bg-white border-b border-gray-200">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">To: Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={directNumber}
                      onChange={(e) => setDirectNumber(e.target.value)}
                      placeholder="(321) 320-9044"
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="flex-1" />

                {/* Compose */}
                <div className="bg-white border-t border-gray-200 px-4 py-3">
                  {error && (
                    <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700">{error}</p>
                    </div>
                  )}
                  {imagePreview && (
                    <div className="relative inline-block mb-2">
                      <img src={imagePreview} alt="Attached" className="max-h-20 rounded-lg border border-gray-200" />
                      {uploading && <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /></div>}
                      <button onClick={removeImage} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} accept="image/*" className="hidden"
                      onChange={(e) => { if (e.target.files?.[0]) uploadImage(e.target.files[0]); e.target.value = ''; }} />
                    <button onClick={() => fileInputRef.current?.click()} title="Attach image"
                      className="self-end p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      placeholder="Type your message... (paste or attach image)"
                      rows={2}
                      maxLength={1600}
                      className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleSend}
                      disabled={(!newMessage.trim() && !imageUrl) || !directNumber.trim() || sending}
                      className={`self-end px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                        (!newMessage.trim() && !imageUrl) || !directNumber.trim() || sending
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : sent
                            ? 'bg-green-600 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : sent ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-[10px] text-gray-400">Enter to send, Shift+Enter for new line</p>
                    <p className={`text-[10px] ${newMessage.length > 160 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {newMessage.length}/160{Math.ceil(newMessage.length / 160) > 1 ? ` (${Math.ceil(newMessage.length / 160)} segments)` : ''}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : selectedPerson ? (
            <>
              {/* Thread Header */}
              <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-blue-700">
                      {(selectedPerson.first_name || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{personName(selectedPerson)}</p>
                    <p className="text-xs text-gray-500">
                      {formatPhone(selectedPerson.phone_number)}
                      {selectedPerson.contact_info && (
                        <span className="ml-1.5 text-purple-600">· via {selectedPerson.contact_info.name} ({selectedPerson.contact_info.relationship})</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAutomated}
                      onChange={(e) => setShowAutomated(e.target.checked)}
                      className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-[10px] text-gray-500">Auto</span>
                  </label>
                  {selectedPerson.client_id && (
                    <button
                      onClick={() => navigate(`/clients/${selectedPerson.client_id}`)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View Profile
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">No messages yet. Send the first one!</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOutgoing = msg.direction === 'outgoing';
                    return (
                      <div key={msg.id} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-lg px-3 py-2 overflow-hidden ${
                          isOutgoing
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-900 border border-gray-200'
                        }`}>
                          {msg.metadata?.media_urls?.length > 0 && (
                            <div className="mb-1">
                              {msg.metadata.media_urls.map((url, idx) => (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt="Media" className="max-w-full rounded max-h-48 cursor-pointer" onError={(e) => { e.target.style.display = 'none'; }} />
                                </a>
                              ))}
                            </div>
                          )}
                          {(msg.metadata?.source || '').startsWith('olivia_') && (
                            <span className="inline-block text-[9px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mb-1">
                              🤖 Generated by AI
                            </span>
                          )}
                          {msg.content && msg.content !== '[Media message]' && (
                            <p className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }}>{msg.content}</p>
                          )}
                          <p className={`text-[10px] mt-1 ${isOutgoing ? 'text-blue-200' : 'text-gray-400'}`}>
                            {formatTime(msg.date_time)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose */}
              <div className="bg-white border-t border-gray-200 px-4 py-3">
                {error && (
                  <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}
                {imagePreview && (
                  <div className="relative inline-block mb-2">
                    <img src={imagePreview} alt="Attached" className="max-h-20 rounded-lg border border-gray-200" />
                    {uploading && <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /></div>}
                    <button onClick={removeImage} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => fileInputRef.current?.click()} title="Attach image"
                    className="self-end p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder="Type your message... (paste or attach image)"
                    rows={2}
                    maxLength={1600}
                    className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSend}
                    disabled={(!newMessage.trim() && !imageUrl) || sending}
                    className={`self-end px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                      (!newMessage.trim() && !imageUrl) || sending
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : sent
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : sent ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="flex justify-between mt-1">
                  <p className="text-[10px] text-gray-400">Enter to send, Shift+Enter for new line</p>
                  <p className={`text-[10px] ${charCount > 160 ? 'text-amber-500' : 'text-gray-400'}`}>
                    {charCount}/160{segments > 1 ? ` (${segments} segments)` : ''}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Select a conversation or search for a contact</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBroadcast(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col" style={{ maxHeight: '85vh' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-red-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Text Team Members</p>
                  <p className="text-xs text-gray-500">
                    {broadcastMode === 'all' ? 'All active team members' : `${selectedTechs.size} selected`}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowBroadcast(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="px-4 py-3 flex-1 overflow-y-auto">
              {/* Mode Toggle */}
              <div className="flex rounded-lg border border-gray-200 mb-3">
                <button
                  onClick={() => setBroadcastMode('all')}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-l-lg transition-colors ${
                    broadcastMode === 'all' ? 'bg-red-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  All Team Members
                </button>
                <button
                  onClick={() => { setBroadcastMode('select'); if (techList.length === 0) fetchTechnicians(); }}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-r-lg transition-colors ${
                    broadcastMode === 'select' ? 'bg-red-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Select Team Members
                </button>
              </div>

              {/* Technician Picker */}
              {broadcastMode === 'select' && (
                <div className="mb-3 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {loadingTechs ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  ) : techList.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No active team members found</p>
                  ) : (
                    techList.map((tech) => {
                      const name = tech.person
                        ? `${tech.person.first_name || ''} ${tech.person.last_name || ''}`.trim()
                        : `Staff #${tech.id}`;
                      const phone = tech.person?.phone_number;
                      const checked = selectedTechs.has(tech.id);
                      return (
                        <label
                          key={tech.id}
                          className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors border-b border-gray-100 last:border-0 ${
                            checked ? 'bg-red-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTech(tech.id)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-red-800 focus:ring-red-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                            {phone && <p className="text-[10px] text-gray-400">{formatPhone(phone)}</p>}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              )}

              {/* Result Messages */}
              {broadcastResult && !broadcastResult.error && (
                <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs font-medium text-green-800">
                    Sent to {broadcastResult.sent_count} team member{broadcastResult.sent_count !== 1 ? 's' : ''}
                  </p>
                  {broadcastResult.failed_count > 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      {broadcastResult.failed_count} failed: {broadcastResult.failed.map(f => f.name).join(', ')}
                    </p>
                  )}
                </div>
              )}
              {broadcastResult?.error && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700">{broadcastResult.error}</p>
                </div>
              )}

              {/* Message Input */}
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                maxLength={737}
                className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                autoFocus
              />
              <div className="flex justify-between mt-1 mb-3">
                <p className="text-[10px] text-gray-400">
                  {broadcastMode === 'all' ? 'Sends to every active team member' : `Sends to ${selectedTechs.size} team member${selectedTechs.size !== 1 ? 's' : ''}`}
                </p>
                <p className={`text-[10px] ${broadcastMessage.length > 600 ? 'text-red-500' : broadcastMessage.length > 160 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {broadcastMessage.length}/737{broadcastMessage.length > 160 ? ` (${Math.ceil(broadcastMessage.length / 160)} segments)` : ''}
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowBroadcast(false)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBroadcast}
                  disabled={!broadcastMessage.trim() || broadcastSending || (broadcastMode === 'select' && selectedTechs.size === 0)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                    !broadcastMessage.trim() || broadcastSending || (broadcastMode === 'select' && selectedTechs.size === 0)
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-red-800 text-white hover:bg-red-900'
                  }`}
                >
                  {broadcastSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {broadcastMode === 'all' ? 'Send to All' : `Send to ${selectedTechs.size}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagingPage;
